import React, { useEffect, useState, useMemo } from "react";
import { Mail, Instagram, Phone, Twitter } from "lucide-react";
import "../styles/footer.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  APP_NAME,
  CONTACT_INFO,
  COPYRIGHTS_INFO,
  EMAIL_INFO,
} from "../constants/info";
import AppLogo from "./AppLogo";
import BaseApi from "../services/base-api";
import { SocialLinkData } from "../pages/NonCustomer/CompanySettingsPage";
import { LinkedIn, QuestionAnswer } from "@mui/icons-material";

// Default hardcoded social links (fallback)
export const defaultSocialLinks = [
  {
    platform: "instagram",
    url: "https://www.instagram.com/",
    displayName: "Instagram",
    sortOrder: 0,
  },
  {
    platform: "tiktok",
    url: "https://www.tiktok.com/",
    displayName: "TikTok",
    sortOrder: 1,
  },
  {
    platform: "twitter",
    url: "https://x.com/home?lang=hi",
    displayName: "Twitter",
    sortOrder: 2,
  },
  {
    platform: "linkedin",
    url: "https://www.linkedin.com/",
    displayName: "LinkedIn",
    sortOrder: 3,
  },
];

// ─── Module-level cache ────────────────────────────────────────────────────────
// Lives outside the component so it persists across mounts/unmounts (page navigations).
// Resets only on a full browser/tab refresh.
let cachedLinks: SocialLinkData[] | null = null;
let cachedETag: string | null = null;
// ──────────────────────────────────────────────────────────────────────────────

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Initialise from cache immediately — no loading flash on repeat visits
  const [socialLinks, setSocialLinks] = useState<SocialLinkData[] | null>(
    cachedLinks,
  );

  useEffect(() => {
    // Cache hit: data already fetched this session — skip the network entirely
    if (cachedLinks !== null) return;

    const fetchSocialLinks = async () => {
      try {
        const headers: Record<string, string> = {};

        // Attach the stored ETag so the server can short-circuit with 304
        if (cachedETag) {
          headers["If-None-Match"] = cachedETag;
        }

        const res = await BaseApi.get("/social-links", { headers });

        // 304 Not Modified — server confirmed nothing changed
        if (res.status === 304) {
          // cachedLinks was already set on a prior call within this tab session;
          // just sync state in case the component remounted before that resolved.
          if (cachedLinks) setSocialLinks(cachedLinks);
          return;
        }

        if (res.success && res.data?.length > 0) {
          // Persist the new ETag for the next conditional request
          if (res.headers?.etag) {
            cachedETag = res.headers.etag;
          }
          cachedLinks = res.data;
          setSocialLinks(res.data);
        } else {
          // Successful response but empty — record as empty so we stop retrying
          cachedLinks = [];
          setSocialLinks([]);
        }
      } catch (error) {
        console.error("Error fetching social links:", error);
        // On network error leave socialLinks as null → defaults will render
      }
    };

    fetchSocialLinks();
  }, []); // Runs once per mount; the cachedLinks guard exits early on every subsequent mount

  // Derive the display list: valid API links → default fallback
  const socialLinksToDisplay = useMemo(() => {
    if (!socialLinks || socialLinks.length === 0) return defaultSocialLinks;

    const active = socialLinks
      .filter((link) => link.url && link.url.trim() !== "")
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    return active.length > 0 ? active : defaultSocialLinks;
  }, [socialLinks]);

  // ─── Icon helpers ────────────────────────────────────────────────────────────
  const TikTokIcon = () => (
    <svg
      className="w-5 h-5 group-hover:scale-110 transition-transform"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );

  const renderSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "instagram":
        return (
          <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
      case "twitter":
        return (
          <Twitter className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
      case "linkedin":
        return (
          <LinkedIn className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
      case "tiktok":
        return <TikTokIcon />;
      default:
        return (
          <QuestionAnswer className="w-5 h-5 group-hover:scale-110 transition-transform" />
        );
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const handleNavigation = (route: string) => navigate(`/${route}`);

  return (
    <footer className="bg-[#f2e6db] border-t border-[#0d223f] text-white">
      <div className="bg-transparent text-gray-800 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-1 order-last lg:order-first transform hover:scale-105 transition-transform duration-300">
              <div className="flex items-center gap-3 mb-4">
                <AppLogo className="w-32 h-32 rounded-2xl lg-shadow" />
              </div>
              {/* <p className="text-gray-600 leading-relaxed mb-6">
                {t("mogaaud_desc")}
              </p> */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-5 h-5" />
                  <span className="font-medium">{t("Email")}</span>
                </div>
                <a
                  href="mailto:info@maagoud.qa"
                  className="text-gray-600 hover:text-black transition-colors duration-300"
                >
                  {EMAIL_INFO}
                </a>
                <div className="flex items-center gap-2 pt-2 text-gray-700">
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">{t("Phone")}</span>
                </div>
                <a
                  href="tel:+97451777107"
                  className="text-gray-600 hover:text-black transition-colors duration-300"
                >
                  <span dir="ltr" className="inline-block">
                    {CONTACT_INFO}
                  </span>
                </a>
              </div>
            </div>

            {/* Follow Us Section */}
            <div className="hidden lg:block transform hover:-translate-y-1 transition-transform duration-300">
              <h3 className="text-xl font-bold mb-6 relative inline-block">
                {t("Follow us on")}
                <span className="absolute bottom-0 left-0 w-12 h-1 bg-black"></span>
              </h3>
              <ul className="space-y-4">
                {socialLinksToDisplay.map((link) => (
                  <li key={link.platform}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-700 hover:text-black transition-all duration-300 group"
                      title={
                        (link as SocialLinkData).description || link.displayName
                      }
                    >
                      {renderSocialIcon(link.platform)}
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {t(link.displayName)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Privacy Links Section */}
            <div className="hidden lg:block transform hover:translate-y-[-4px] transition-transform duration-300">
              <h3 className="text-xl font-bold mb-6 relative inline-block">
                {t("Privacy links")}
                <span className="absolute bottom-0 left-0 w-12 h-1 bg-black"></span>
              </h3>
              <ul className="space-y-4">
                <li>
                  <div
                    className="text-gray-700 hover:text-black transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                    onClick={() => handleNavigation("privacy-policy")}
                  >
                    {t("Exchange and Return Policy")}
                  </div>
                </li>
              </ul>
            </div>

            {/* Quick Links Section */}
            <div className="hidden lg:block transform hover:translate-y-[-4px] transition-transform duration-300">
              <h3 className="text-xl font-bold mb-6 relative inline-block">
                {t("Quick links")}
                <span className="absolute bottom-0 left-0 w-12 h-1 bg-black"></span>
              </h3>
              <ul className="space-y-4">
                {[
                  { label: "Home", route: "home" },
                  { label: "About us", route: "about" },
                  { label: "Store", route: "" },
                  { label: "Contact us", route: "contact" },
                ].map(({ label, route }) => (
                  <li key={label}>
                    <div
                      className="text-gray-700 hover:text-black transition-all duration-300 inline-block hover:translate-x-1 cursor-pointer"
                      onClick={() => handleNavigation(route)}
                    >
                      {t(label)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-[#0d223f] pt-8 text-center">
            <p className="text-gray-600">
              {APP_NAME}
              {t(". All rights reserved")}
              {COPYRIGHTS_INFO}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
